import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { MeshGradient } from "../components/AnimatedBackground";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../i18n";

export function Privacy() {
  const { t, lang, switchLanguage } = useLanguage();

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="legal" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Top bar: back + language switch */}
          <div className="flex items-center justify-between mb-8">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToHome}
            </a>
            <button
              onClick={() => switchLanguage(lang === "de" ? "en" : "de")}
              className="text-sm text-zinc-500 hover:text-white transition-colors border border-white/10 rounded px-2 py-1"
            >
              {lang === "de" ? "English" : "Deutsch"}
            </button>
          </div>

          {/* Page Header */}
          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">{t.datenschutz.title}</h1>
            <p className="text-zinc-400">{t.datenschutz.subtitle}</p>
          </div>

          {/* Content */}
          <div className="legal-content space-y-8">
            {/* 1. Controller */}
            <section>
              <h2>{t.datenschutz.section1Title}</h2>
              <p>{t.datenschutz.section1Text}</p>
              <div className="ml-4 space-y-1">
                <p className="text-zinc-400">{t.datenschutz.name}</p>
                <p className="text-zinc-400">{t.datenschutz.address1}</p>
                <p className="text-zinc-400">{t.datenschutz.address2}</p>
                <p className="text-zinc-400">{t.datenschutz.address3}</p>
                <p className="text-zinc-400">{t.datenschutz.address4}</p>
                <p className="text-zinc-400">
                  <a href={`mailto:${t.datenschutz.contactEmail}`} className="text-emerald-400 hover:text-emerald-300 transition-colors">
                    {t.datenschutz.contactEmail}
                  </a>
                </p>
              </div>
            </section>

            {/* 2. Overview */}
            <section>
              <h2>{t.datenschutz.section2Title}</h2>
              <p>{t.datenschutz.section2Intro}</p>

              {/* 2a */}
              <h3>{t.datenschutz.processing1Title}</h3>
              <p>{t.datenschutz.processing1Data}</p>
              <p>{t.datenschutz.processing1Purpose}</p>
              <p>{t.datenschutz.processing1Basis}</p>
              <p>{t.datenschutz.processing1Retention}</p>

              {/* 2b */}
              <h3>{t.datenschutz.processing2Title}</h3>
              <p>{t.datenschutz.processing2Data}</p>
              <p>{t.datenschutz.processing2Purpose}</p>
              <p>{t.datenschutz.processing2Basis}</p>
              <p>{t.datenschutz.processing2Retention}</p>

              {/* 2c */}
              <h3>{t.datenschutz.processing3Title}</h3>
              <p>{t.datenschutz.processing3Data}</p>
              <p>{t.datenschutz.processing3Purpose}</p>
              <p>{t.datenschutz.processing3Basis}</p>
              <p>{t.datenschutz.processing3Retention}</p>

              {/* 2d */}
              <h3>{t.datenschutz.processing4Title}</h3>
              <p>{t.datenschutz.processing4Data}</p>
              <p>{t.datenschutz.processing4Purpose}</p>
              <p>{t.datenschutz.processing4Basis}</p>
              <p>{t.datenschutz.processing4Retention}</p>

              {/* 2e */}
              <h3>{t.datenschutz.processing5Title}</h3>
              <p>{t.datenschutz.processing5Data}</p>
              <p>{t.datenschutz.processing5Purpose}</p>
              <p>{t.datenschutz.processing5Basis}</p>
              <p>{t.datenschutz.processing5Retention}</p>

              {/* 2f */}
              <h3>{t.datenschutz.processing6Title}</h3>
              <p>{t.datenschutz.processing6Data}</p>
              <p>{t.datenschutz.processing6Purpose}</p>
              <p>{t.datenschutz.processing6Basis}</p>
              <p>{t.datenschutz.processing6Retention}</p>

              {/* 2g */}
              <h3>{t.datenschutz.processing7Title}</h3>
              <p>{t.datenschutz.processing7Data}</p>
              <p>{t.datenschutz.processing7Purpose}</p>
              <p>{t.datenschutz.processing7Basis}</p>
              <p>{t.datenschutz.processing7Retention}</p>

              {/* 2h */}
              <h3>{t.datenschutz.processing8Title}</h3>
              <p>{t.datenschutz.processing8Data}</p>
              <p>{t.datenschutz.processing8Purpose}</p>
              <p>{t.datenschutz.processing8Basis}</p>
              <p>{t.datenschutz.processing8Retention}</p>
            </section>

            {/* 3. Recipients */}
            <section>
              <h2>{t.datenschutz.section3Title}</h2>
              <p>{t.datenschutz.section3P1}</p>
              <ul>
                <li>{t.datenschutz.recipient1}</li>
                <li>{t.datenschutz.recipient2}</li>
                <li>{t.datenschutz.recipient3}</li>
                <li>{t.datenschutz.recipient4}</li>
              </ul>
            </section>

            {/* 4. Third Countries */}
            <section>
              <h2>{t.datenschutz.section4Title}</h2>
              <p>{t.datenschutz.section4P1}</p>
            </section>

            {/* 5. Retention */}
            <section>
              <h2>{t.datenschutz.section5Title}</h2>
              <p>{t.datenschutz.section5P1}</p>
            </section>

            {/* 6. Rights */}
            <section>
              <h2>{t.datenschutz.section6Title}</h2>
              <p>{t.datenschutz.section6P1}</p>
              <ul>
                <li>{t.datenschutz.right1}</li>
                <li>{t.datenschutz.right2}</li>
                <li>{t.datenschutz.right3}</li>
                <li>{t.datenschutz.right4}</li>
                <li>{t.datenschutz.right5}</li>
                <li>{t.datenschutz.right6}</li>
                <li>{t.datenschutz.right7}</li>
              </ul>
              <p>{t.datenschutz.section6P2}</p>
            </section>

            {/* 7. Complaint Right */}
            <section>
              <h2>{t.datenschutz.section7Title}</h2>
              <p>{t.datenschutz.section7P1}</p>
              <p>{t.datenschutz.section7P2}</p>
              <div className="ml-4 space-y-1">
                <p className="text-zinc-400">{t.datenschutz.authorityName}</p>
                <p className="text-zinc-400">{t.datenschutz.authorityAddress}</p>
                <p className="text-zinc-400">
                  <a href={t.datenschutz.authorityWebsite} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                    {t.datenschutz.authorityWebsite}
                  </a>
                </p>
              </div>
            </section>

            {/* 8. Cookies */}
            <section>
              <h2>{t.datenschutz.section8Title}</h2>
              <p>{t.datenschutz.section8P1}</p>
            </section>

            {/* 9. Security */}
            <section>
              <h2>{t.datenschutz.section9Title}</h2>
              <p>{t.datenschutz.section9P1}</p>
              <ul>
                <li>{t.datenschutz.security1}</li>
                <li>{t.datenschutz.security2}</li>
                <li>{t.datenschutz.security3}</li>
                <li>{t.datenschutz.security4}</li>
                <li>{t.datenschutz.security5}</li>
              </ul>
            </section>

            {/* 10. Changes */}
            <section>
              <h2>{t.datenschutz.section10Title}</h2>
              <p>{t.datenschutz.section10P1}</p>
            </section>

            {/* 11. Contact */}
            <section>
              <h2>{t.datenschutz.section11Title}</h2>
              <p>{t.datenschutz.section11P1}</p>
              <p>
                <a href={`mailto:${t.datenschutz.contactEmail}`} className="text-emerald-400 hover:text-emerald-300 transition-colors">
                  {t.datenschutz.contactEmail}
                </a>
              </p>
            </section>
          </div>

          {/* Last updated */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-sm text-zinc-500">Last updated: April 2026</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
